import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {useAuth} from '../authentication/AuthProvider'
import {getSuperAdminRole} from '../authentication/AuthUtils'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    appNotificationEvent,
    removeAppNotificationListener,
} from './event/NotificationEvent'
import {NotificationTypes} from './NotificationConstant'
import {
    useCountCustomNotificationList,
    useCustomPageNotifications,
} from './query-keys/NotificationKeysManager'

const NotificationContext = createContext({})

const InitialPageable = {
    pageNumber: 0,
    pageRows: 5,
    sortField: 'created_at',
    sortOrder: -1,
}

const NotificationProvider = React.memo(({children}) => {
    useSignals()
    const {user} = useAuth()
    const {userRole, notificationSetting, timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()

    const queryClient = useQueryClient()

    const pagable = useSignal({
        fall: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
        news: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
        emergency: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
        symptom: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
        conversation: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
        ecg: {
            pageNumber: 0,
            pageRows: 5,
            sortField: 'created_at',
            sortOrder: -1,
        },
    })

    const pageNumberRef = useRef({
        fall: 0,
        news: 0,
        emergency: 0,
    })

    const handleChangePageNumber = useCallback(
        (type, pageNumber) => {
            pageNumberRef.current[type] = pageNumberRef.current[type] + 1
            pagable.value[type] = {
                ...pagable.value[type],
                pageNumber: pagable.value[type]?.pageNumber + 1,
            }
        },
        [pagable.value],
    )

    // returns the query keys for the notification
    const getNotificationQueryKeys = useCallback(
        (type, pageNumber) => {
            return {
                userId: user?.id,
                hospitalId: currentHospital?.id,
                type: type || null,
                userRole: userRole,
                timezoneId: timezoneId,
                pageNumber: pageNumber || InitialPageable.pageNumber,
                pageRows: InitialPageable.pageRows,
                sortField: InitialPageable.sortField,
                sortOrder: InitialPageable.sortOrder,
                filterRequest: {
                    hospitalId: currentHospital?.id,
                    type: type || null,
                },
                enabled: notificationSetting?.hospitals?.includes(
                    currentHospital?.id,
                ),
            }
        },
        [notificationSetting, userRole, currentHospital, user],
    )

    const {data: fallNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.FALL,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.FALL,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    const {data: symptomNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.SYMPTOM,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.SYMPTOM,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    const {data: newsNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.NEWS,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.NEWS,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    const {data: emergencyNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.EMERGENCY,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.EMERGENCY,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    const {data: ecgNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.ECG,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.ECG,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    const {data: conversationNotifications} = useCustomPageNotifications(
        user?.id,
        currentHospital?.id,
        NotificationTypes.CONVERSATION,
        userRole,
        timezoneId,
        InitialPageable.pageNumber,
        InitialPageable.pageRows,
        InitialPageable.sortField,
        InitialPageable.sortOrder,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.CONVERSATION,
        },
        notificationSetting?.hospitals?.includes(currentHospital?.id) &&
            userRole !== SystemRole.USER &&
            !getSuperAdminRole(),
    )

    //#region Total Count
    //#region NEWS
    const {data: totalNewsRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.NEWS,
        },
        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const newsQueryData = useMemo(() => {
        return {
            content: newsNotifications || [],
            numberOfElements: isArrayAndNotEmpty(newsNotifications)
                ? newsNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.NEWS].pageNumber,
            totalUnseen: totalNewsRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalNewsRecords?.totalNotifications /
                    pagable.value[NotificationTypes.NEWS].pageRows,
            ),
            totalElements: totalNewsRecords,
            size: pagable.value[NotificationTypes.NEWS].pageRows,
            first: pagable.value[NotificationTypes.NEWS].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.NEWS].pageNumber ===
                Math.ceil(
                    totalNewsRecords?.totalNotifications /
                        pagable.value[NotificationTypes.NEWS].pageRows,
                ) -
                    1,
        }
    }, [newsNotifications, totalNewsRecords, pagable.value])
    //#endregion

    //#region FALL
    const {data: totalFallRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.FALL,
        },
        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const fallQueryData = useMemo(() => {
        return {
            content: fallNotifications || [],
            numberOfElements: isArrayAndNotEmpty(fallNotifications)
                ? fallNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.FALL].pageNumber,
            totalUnseen: totalFallRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalFallRecords?.totalNotifications /
                    pagable.value[NotificationTypes.FALL].pageRows,
            ),
            totalElements: totalFallRecords,
            size: pagable.value[NotificationTypes.FALL].pageRows,
            first: pagable.value[NotificationTypes.FALL].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.FALL].pageNumber ===
                Math.ceil(
                    totalFallRecords?.totalNotifications /
                        pagable.value[NotificationTypes.FALL].pageRows,
                ) -
                    1,
        }
    }, [fallNotifications, totalFallRecords, pagable.value])
    //#endregion

    //#region EMERGENCY
    const {data: totalEmergencyRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.EMERGENCY,
        },

        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const emergencyQueryData = useMemo(() => {
        return {
            content: emergencyNotifications || [],
            numberOfElements: isArrayAndNotEmpty(emergencyNotifications)
                ? emergencyNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.EMERGENCY].pageNumber,
            totalUnseen: totalEmergencyRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalEmergencyRecords?.totalNotifications /
                    pagable.value[NotificationTypes.EMERGENCY].pageRows,
            ),
            totalElements: totalEmergencyRecords?.totalNotifications,
            size: pagable.value[NotificationTypes.EMERGENCY].pageRows,
            first: pagable.value[NotificationTypes.EMERGENCY].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.EMERGENCY].pageNumber ===
                Math.ceil(
                    totalEmergencyRecords?.totalNotifications /
                        pagable.value[NotificationTypes.EMERGENCY].pageRows,
                ) -
                    1,
        }
    }, [emergencyNotifications, totalEmergencyRecords, pagable.value])
    //#endregion

    //#region SYMPTOM
    const {data: totalSymptomRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.SYMPTOM,
        },
        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const symptomQueryData = useMemo(() => {
        return {
            content: symptomNotifications || [],
            numberOfElements: isArrayAndNotEmpty(symptomNotifications)
                ? symptomNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.SYMPTOM].pageNumber,
            totalUnseen: totalSymptomRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalSymptomRecords?.totalNotifications /
                    pagable.value[NotificationTypes.SYMPTOM].pageRows,
            ),
            totalElements: totalSymptomRecords?.totalNotifications,
            size: pagable.value[NotificationTypes.SYMPTOM].pageRows,
            first: pagable.value[NotificationTypes.SYMPTOM].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.SYMPTOM].pageNumber ===
                Math.ceil(
                    totalSymptomRecords?.totalNotifications /
                        pagable.value[NotificationTypes.SYMPTOM].pageRows,
                ) -
                    1,
        }
    }, [symptomNotifications, totalSymptomRecords, pagable.value])
    //#endregion

    //#region SEIZURE
    // const {data: totalSeizureRecords} = useCountCustomNotificationList(
    //     userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
    //     timezoneId,
    //     user?.id,
    //     {
    //         hospitalId: currentHospital?.id,
    //         type: NotificationTypes.SEIZURE,
    //     },
    //                 userRole !== SystemRole.USER &&
    // !getSuperAdminRole(),

    // )

    // const seizureQueryData = useMemo(() => {
    //     return {
    //         content: seizureNotifications || [],
    //         numberOfElements: isArrayAndNotEmpty(seizureNotifications)
    //             ? seizureNotifications?.length
    //             : 0,
    //         number: pageNumberRef.current[NotificationTypes.SEIZURE],

    //         totalPages: Math.ceil(
    //             totalSeizureRecords / InitialPageable.pageRows,
    //         ),
    //         totalElements: totalSeizureRecords,
    //         size: InitialPageable.pageRows,
    //         first: pageNumberRef.current[NotificationTypes.SEIZURE] === 0,
    //         last:
    //             pageNumberRef.current[NotificationTypes.SEIZURE] ===
    //             Math.ceil(totalSeizureRecords / InitialPageable.pageRows) - 1,
    //     }
    // }, [seizureNotifications, totalSeizureRecords])
    //#endregion

    //#region CONVERSATION
    const {data: totalConversationRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.CONVERSATION,
        },
        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const conversationQueryData = useMemo(() => {
        return {
            content: conversationNotifications || [],
            numberOfElements: isArrayAndNotEmpty(conversationNotifications)
                ? conversationNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.CONVERSATION].pageNumber,
            totalUnseen:
                totalConversationRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalConversationRecords?.totalNotifications /
                    pagable.value[NotificationTypes.CONVERSATION].pageRows,
            ),
            totalElements: totalConversationRecords?.totalNotifications,
            size: pagable.value[NotificationTypes.CONVERSATION].pageRows,
            first:
                pagable.value[NotificationTypes.CONVERSATION].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.CONVERSATION].pageNumber ===
                Math.ceil(
                    totalConversationRecords?.totalNotifications /
                        pagable.value[NotificationTypes.CONVERSATION].pageRows,
                ) -
                    1,
        }
    }, [conversationNotifications, totalConversationRecords, pagable.value])
    //#endregion

    //#region ECG
    const {data: totalECGRecords} = useCountCustomNotificationList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        {
            hospitalId: currentHospital?.id,
            type: NotificationTypes.ECG,
        },
        userRole !== SystemRole.USER && !getSuperAdminRole(),
    )

    const ecgQueryData = useMemo(() => {
        return {
            content: ecgNotifications || [],
            numberOfElements: isArrayAndNotEmpty(ecgNotifications)
                ? ecgNotifications?.length
                : 0,
            number: pagable.value[NotificationTypes.ECG].pageNumber,
            totalUnseen: totalECGRecords?.totalUnseenNotifications || 0,
            totalPages: Math.ceil(
                totalECGRecords?.totalNotifications /
                    pagable.value[NotificationTypes.ECG].pageRows,
            ),
            totalElements: totalECGRecords?.totalNotifications,
            size: pagable.value[NotificationTypes.ECG].pageRows,
            first: pagable.value[NotificationTypes.ECG].pageNumber === 0,
            last:
                pagable.value[NotificationTypes.ECG].pageNumber ===
                Math.ceil(
                    totalECGRecords?.totalNotifications /
                        pagable.value[NotificationTypes.ECG].pageRows,
                ) -
                    1,
        }
    }, [ecgNotifications, totalECGRecords, pagable.value])
    //#endregion

    //#endregion

    const totalNotifications = useMemo(() => {
        return (
            (fallQueryData?.totalElements || 0) +
            (newsQueryData?.totalElements || 0) +
            (emergencyQueryData?.totalElements || 0) +
            (symptomQueryData?.totalElements || 0) +
            (conversationQueryData?.totalElements || 0) +
            (ecgQueryData?.totalElements || 0)
        )
    }, [
        fallQueryData,
        newsQueryData,
        emergencyQueryData,
        symptomQueryData,
        conversationQueryData,
        ecgQueryData,
    ])

    const totalUnseenNotifications = useMemo(() => {
        return (
            (fallQueryData?.totalUnseen || 0) +
            (newsQueryData?.totalUnseen || 0) +
            (emergencyQueryData?.totalUnseen || 0) +
            (symptomQueryData?.totalUnseen || 0) +
            (conversationQueryData?.totalUnseen || 0) +
            (ecgQueryData?.totalUnseen || 0)
        )
    }, [
        fallQueryData,
        newsQueryData,
        emergencyQueryData,
        symptomQueryData,
        conversationQueryData,
        ecgQueryData,
    ])

    //#region Reload Notifications when Notification Settings are changed
    useMemo(() => {
        if (!!notificationSetting) {
            queryClient.invalidateQueries(QueryKeyTypes.notification)
        }
    }, [notificationSetting])
    //#endregion

    //#region New Notification Reload Listener
    useEffect(() => {
        appNotificationEvent.on('RELOAD_NOTIFICATIONS', payload => {
            if (
                !notificationSetting ||
                !isArrayAndNotEmpty(notificationSetting?.patients) ||
                (payload?.userId &&
                    !notificationSetting?.patients?.includes(payload?.userId))
            ) {
                return
            }

            queryClient.invalidateQueries(QueryKeyTypes.notification)
        })

        return () => {
            removeAppNotificationListener()
        }
    }, [notificationSetting])
    //#endregion

    return (
        <NotificationContext.Provider
            value={{
                totalNotifications: totalNotifications,
                totalUnseenNotifications: totalUnseenNotifications,

                fallNotifications: fallQueryData,
                newsNotifications: newsQueryData,
                emergencyNotifications: emergencyQueryData,
                symptomNotifications: symptomQueryData,
                conversationNotifications: conversationQueryData,
                ecgNotifications: ecgQueryData,
                notificationPageNumber: pageNumberRef.current,

                getNotificationQueryKeys,
                onChangeNotificationPageNumber: handleChangePageNumber,
            }}>
            {children}
        </NotificationContext.Provider>
    )
})

export const useNotificationContext = () => useContext(NotificationContext)
export default NotificationProvider
